import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Input } from "@/components/ui/input";
import { ChevronDown, Search } from "lucide-react";

// TrustWallet assets URL mapping
const TRUSTWALLET_BASE = "https://raw.githubusercontent.com/trustwallet/assets/master/blockchains";

// Map coin_id/symbol to TrustWallet blockchain and contract address
const getCoinIcon = (coinId, symbol, network) => {
  const symbolUpper = symbol?.toUpperCase();
  const coinIdLower = coinId?.toLowerCase();
  
  // Native coins mapping
  const nativeCoins = {
    'bitcoin': `${TRUSTWALLET_BASE}/bitcoin/info/logo.png`,
    'btc': `${TRUSTWALLET_BASE}/bitcoin/info/logo.png`,
    'ethereum': `${TRUSTWALLET_BASE}/ethereum/info/logo.png`,
    'eth': `${TRUSTWALLET_BASE}/ethereum/info/logo.png`,
    'litecoin': `${TRUSTWALLET_BASE}/litecoin/info/logo.png`,
    'ltc': `${TRUSTWALLET_BASE}/litecoin/info/logo.png`,
    'ripple': `${TRUSTWALLET_BASE}/ripple/info/logo.png`,
    'xrp': `${TRUSTWALLET_BASE}/ripple/info/logo.png`,
    'dogecoin': `${TRUSTWALLET_BASE}/doge/info/logo.png`,
    'doge': `${TRUSTWALLET_BASE}/doge/info/logo.png`,
    'tron': `${TRUSTWALLET_BASE}/tron/info/logo.png`,
    'trx': `${TRUSTWALLET_BASE}/tron/info/logo.png`,
    'solana': `${TRUSTWALLET_BASE}/solana/info/logo.png`,
    'sol': `${TRUSTWALLET_BASE}/solana/info/logo.png`,
    'cardano': `${TRUSTWALLET_BASE}/cardano/info/logo.png`,
    'ada': `${TRUSTWALLET_BASE}/cardano/info/logo.png`,
    'polkadot': `${TRUSTWALLET_BASE}/polkadot/info/logo.png`,
    'dot': `${TRUSTWALLET_BASE}/polkadot/info/logo.png`,
    'bnb': `${TRUSTWALLET_BASE}/binance/info/logo.png`,
    'binancecoin': `${TRUSTWALLET_BASE}/binance/info/logo.png`,
    'polygon': `${TRUSTWALLET_BASE}/polygon/info/logo.png`,
    'matic': `${TRUSTWALLET_BASE}/polygon/info/logo.png`,
    'avalanche': `${TRUSTWALLET_BASE}/avalanchec/info/logo.png`,
    'avax': `${TRUSTWALLET_BASE}/avalanchec/info/logo.png`,
    'cosmos': `${TRUSTWALLET_BASE}/cosmos/info/logo.png`,
    'atom': `${TRUSTWALLET_BASE}/cosmos/info/logo.png`,
    'monero': `${TRUSTWALLET_BASE}/monero/info/logo.png`,
    'xmr': `${TRUSTWALLET_BASE}/monero/info/logo.png`,
    'stellar': `${TRUSTWALLET_BASE}/stellar/info/logo.png`,
    'xlm': `${TRUSTWALLET_BASE}/stellar/info/logo.png`,
    'ton': `${TRUSTWALLET_BASE}/ton/info/logo.png`,
    'toncoin': `${TRUSTWALLET_BASE}/ton/info/logo.png`,
  };

  // USDT on different networks
  const usdtContracts = {
    'ERC20': `${TRUSTWALLET_BASE}/ethereum/assets/0xdAC17F958D2ee523a2206206994597C13D831ec7/logo.png`,
    'TRC20': `${TRUSTWALLET_BASE}/tron/assets/TR7NHqjeKQxGTCi8q8ZY4pL8otSzgjLj6t/logo.png`,
    'BEP20': `${TRUSTWALLET_BASE}/smartchain/assets/0x55d398326f99059fF775485246999027B3197955/logo.png`,
    'SOL': `${TRUSTWALLET_BASE}/solana/assets/Es9vMFrzaCERmJfrF4H2FYD4KCoNkY11McCe8BenwNYB/logo.png`,
    'POLYGON': `${TRUSTWALLET_BASE}/polygon/assets/0xc2132D05D31c914a87C6611C10748AEb04B58e8F/logo.png`,
  };

  // USDC on different networks
  const usdcContracts = {
    'ERC20': `${TRUSTWALLET_BASE}/ethereum/assets/0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48/logo.png`,
    'TRC20': `${TRUSTWALLET_BASE}/tron/assets/TEkxiTehnzSmSe2XqrBj4w32RUN966rdz8/logo.png`,
    'BEP20': `${TRUSTWALLET_BASE}/smartchain/assets/0x8AC76a51cc950d9822D68b83fE1Ad97B32Cd580d/logo.png`,
    'SOL': `${TRUSTWALLET_BASE}/solana/assets/EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v/logo.png`,
    'POLYGON': `${TRUSTWALLET_BASE}/polygon/assets/0x2791Bca1f2de4661ED88A30C99A7a9449Aa84174/logo.png`,
  };

  // SHIB
  const shibIcon = `${TRUSTWALLET_BASE}/ethereum/assets/0x95aD61b0a150d79219dCF64E1E6Cc01f0B64C4cE/logo.png`;

  // Check for USDT
  if (symbolUpper === 'USDT' || coinIdLower?.includes('tether')) {
    const networkUpper = network?.toUpperCase() || '';
    if (networkUpper.includes('TRC') || coinIdLower?.includes('trc20')) return usdtContracts['TRC20'];
    if (networkUpper.includes('ERC') || coinIdLower?.includes('erc20')) return usdtContracts['ERC20'];
    if (networkUpper.includes('BEP') || coinIdLower?.includes('bep20')) return usdtContracts['BEP20'];
    if (networkUpper.includes('SOL')) return usdtContracts['SOL'];
    if (networkUpper.includes('POLYGON')) return usdtContracts['POLYGON'];
    return usdtContracts['ERC20']; // Default to ERC20
  }

  // Check for USDC
  if (symbolUpper === 'USDC' || coinIdLower?.includes('usd-coin')) {
    const networkUpper = network?.toUpperCase() || '';
    if (networkUpper.includes('TRC')) return usdcContracts['TRC20'];
    if (networkUpper.includes('ERC')) return usdcContracts['ERC20'];
    if (networkUpper.includes('BEP')) return usdcContracts['BEP20'];
    if (networkUpper.includes('SOL')) return usdcContracts['SOL'];
    if (networkUpper.includes('POLYGON')) return usdcContracts['POLYGON'];
    return usdcContracts['ERC20'];
  }

  // Check for SHIB
  if (symbolUpper === 'SHIB' || coinIdLower?.includes('shib')) {
    return shibIcon;
  }

  // Check native coins by coin_id
  if (coinIdLower && nativeCoins[coinIdLower]) {
    return nativeCoins[coinIdLower];
  }

  // Check native coins by symbol
  if (symbolUpper && nativeCoins[symbolUpper.toLowerCase()]) {
    return nativeCoins[symbolUpper.toLowerCase()];
  }

  // Fallback - return null to show text icon
  return null;
};

// CoinIcon component with fallback
function CoinIcon({ coinId, symbol, network, size = 32, className = "" }) {
  const [imgError, setImgError] = useState(false);
  const iconUrl = getCoinIcon(coinId, symbol, network);

  if (!iconUrl || imgError) {
    // Fallback to text-based icon
    return (
      <div 
        className={`flex items-center justify-center rounded-full bg-gradient-to-br from-blue-500 to-cyan-500 text-white font-bold ${className}`}
        style={{ width: size, height: size, fontSize: size * 0.45 }}
      >
        {symbol?.slice(0, 2).toUpperCase() || '?'}
      </div>
    );
  }

  return (
    <img
      src={iconUrl}
      alt={symbol}
      className={`rounded-full ${className}`}
      style={{ width: size, height: size }}
      onError={() => setImgError(true)}
    />
  );
}

function CurrencySelector({ currencies, selected, onSelect }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  const filteredCurrencies = currencies.filter(
    (c) =>
      c.name.toLowerCase().includes(search.toLowerCase()) ||
      c.symbol.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className="w-[140px] md:w-[180px] justify-between border-slate-200 hover:border-blue-400 bg-slate-50 h-11 md:h-12"
          data-testid="currency-selector-trigger"
        >
          {selected ? (
            <div className="flex items-center gap-2">
              <CoinIcon 
                coinId={selected.coin_id} 
                symbol={selected.symbol} 
                network={selected.network}
                size={24}
              />
              <span className="font-mono font-bold text-sm md:text-base">{selected.symbol}</span>
            </div>
          ) : (
            <span className="text-slate-500">Выберите</span>
          )}
          <ChevronDown size={16} className="text-slate-400" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[300px] p-0 bg-white border-slate-200 shadow-xl" data-testid="currency-selector-popover">
        <div className="p-2 border-b border-slate-100">
          <div className="relative">
            <Search className="absolute left-3 top-2.5 text-slate-400" size={16} />
            <Input
              placeholder="Поиск валюты..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 h-10 bg-slate-50 border-slate-200 focus:border-blue-400"
              data-testid="currency-search-input"
            />
          </div>
        </div>
        <div className="max-h-[300px] overflow-y-auto">
          {filteredCurrencies.length === 0 ? (
            <div className="px-4 py-8 text-center text-slate-400 text-sm">
              Ничего не найдено
            </div>
          ) : (
            filteredCurrencies.map((currency) => (
              <button
                key={currency.coin_id}
                onClick={() => {
                  onSelect(currency);
                  setOpen(false);
                  setSearch("");
                }}
                className="w-full flex items-center gap-3 px-4 py-3 hover:bg-blue-50 transition-colors text-left border-b border-slate-50 last:border-b-0"
                data-testid={`currency-option-${currency.coin_id}`}
              >
                <CoinIcon 
                  coinId={currency.coin_id} 
                  symbol={currency.symbol} 
                  network={currency.network}
                  size={36}
                />
                <div className="flex-1 min-w-0">
                  <div className="font-bold font-mono text-slate-800">{currency.symbol}</div>
                  <div className="text-xs text-slate-500 truncate">{currency.name}</div>
                  {currency.network && (
                    <div className="text-xs text-blue-500 font-mono">{currency.network}</div>
                  )}
                </div>
                <div className="text-xs text-slate-400 font-mono">
                  ${(currency.use_custom_rate ? currency.custom_rate : currency.rate_usd)?.toFixed(2) || '0.00'}
                </div>
              </button>
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

export { CoinIcon, getCoinIcon };
export default CurrencySelector;
